import { navigate } from '../../shell/router'
import { useT } from '../../i18n'
import type { MessageKey } from '../../i18n'

/**
 * Pièces communes aux deux jeux de prose du guide.
 *
 * Elles ne portent aucun texte : ce sont les sections qui le fournissent, dans
 * leur langue. Seul le schéma a des étiquettes, prises au catalogue.
 */

export interface GuideSection {
  /** Ancre stable : un lien envoyé à un collègue ne doit pas changer de langue. */
  id: string
  titleKey: MessageKey
  body: React.ReactNode
}

/** Renvoi vers un écran de l'outil, depuis le corps du guide. */
export function Go({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <button type="button" className="guide-link" onClick={() => navigate(to)}>
      {children}
    </button>
  )
}

export function Table({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="guide-table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} scope="col">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Circuit de traitement d'une détection.
 *
 * Les couleurs passent par des classes CSS et non par des attributs de
 * présentation : `fill="var(--border)"` ne résout pas les variables CSS, et le
 * schéma doit suivre le thème clair comme le thème sombre.
 *
 * Les étiquettes sont du texte dans le SVG : elles viennent du catalogue, et
 * les boîtes sont assez larges pour les deux langues.
 */
export function TriageFlow() {
  const t = useT()
  const BW = 120
  const BH = 44
  const rowA = 88
  const rowB = 208
  const cols = { nouveau: 84, examiner: 232, encours: 380, confirme: 528, archive: 676 }

  const state = (cx: number, cy: number, id: string, label: string, closing: boolean) => (
    <g key={id}>
      <rect
        className={closing ? 'guide-flow-node guide-flow-node-closing' : 'guide-flow-node'}
        x={cx - BW / 2}
        y={cy - BH / 2}
        width={BW}
        height={BH}
        rx="8"
      />
      <text x={cx} y={cy + 4} textAnchor="middle" className="guide-flow-label">
        {label}
      </text>
    </g>
  )

  return (
    <figure className="guide-figure">
      <svg viewBox="0 0 760 330" role="img" aria-label={t('guide.flow.aria')} className="guide-flow">
        <defs>
          <marker id="guide-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0 0L10 5L0 10z" />
          </marker>
        </defs>

        <g className="guide-flow-edges">
          <path d="M144 88H172" markerEnd="url(#guide-arrow)" />
          <path d="M292 88H320" markerEnd="url(#guide-arrow)" />
          <path d="M440 88H468" markerEnd="url(#guide-arrow)" />
          <path d="M588 88H616" markerEnd="url(#guide-arrow)" />
          <path d="M380 110V186" markerEnd="url(#guide-arrow)" />
          <path d="M510 110V186" markerEnd="url(#guide-arrow)" />
          <path d="M546 186V110" markerEnd="url(#guide-arrow)" />
          <path d="M380 230V284H676V110" markerEnd="url(#guide-arrow)" />
          <path d="M588 208H640V110" markerEnd="url(#guide-arrow)" />
        </g>
        <path className="guide-flow-edge-dashed" d="M676 66V40H232V66" markerEnd="url(#guide-arrow)" />
        <text x="454" y="32" textAnchor="middle" className="guide-flow-note">{t('guide.flow.reopen')}</text>

        {state(cols.nouveau, rowA, 'nouveau', t('triage.NEW'), false)}
        {state(cols.examiner, rowA, 'examiner', t('triage.TO_REVIEW'), false)}
        {state(cols.encours, rowA, 'encours', t('triage.IN_REVIEW'), false)}
        {state(cols.confirme, rowA, 'confirme', t('triage.CONFIRMED'), true)}
        {state(cols.archive, rowA, 'archive', t('triage.ARCHIVED'), true)}
        {state(cols.encours, rowB, 'fauxpositif', t('triage.FALSE_POSITIVE'), true)}
        {state(cols.confirme, rowB, 'escalade', t('triage.ESCALATED'), true)}

        <g>
          <rect className="guide-flow-node guide-flow-node-closing" x="24" y="300" width="18" height="12" rx="3" />
          <text x="50" y="310" className="guide-flow-note">{t('guide.flow.reasonRequired')}</text>
        </g>
      </svg>
      <figcaption>{t('guide.flow.caption')}</figcaption>
    </figure>
  )
}
